import React from "react";
import { Chip } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { formatRestriction } from "@features/vessel-call/grid/utils";
import type { VesselHeaderDTO } from "@features/vessel-call/vessel-call-status/VesselCallStatus.types.tsx";
import type { FromToLocation } from "@services/vessel-call/types";
import { capitalizeWord } from "@utils/string";
import type { TFunction } from "i18next";
import type { VesselCallNotificationDTO } from "./VesselCallStatus.types";

type BadgeType = "success" | "warning" | "error" | "neutral";

const LANG_BASE = "vessel-call";

interface StatusMappingEntry {
  label: string;
  badgeType: BadgeType;
}

export const DEPARTURE_STATUSES = new Set([
  "DEPARTURE_REQUESTED",
  "DEPARTURE_AWAITING_MARINE_APPROVAL",
  "DEPARTURE_APPROVED",
  "DEPARTURE_AWAITING_TERMINAL_APPROVAL",
]);

export const TO_BERTH_TYPES = new Set(["SEA_TO_BERTH", "ANCHORAGE_TO_BERTH", "BERTH_TO_BERTH"]);

const STATUS_MAP: Record<string, StatusMappingEntry> = {
  PENDING_PRE_APPROVAL: { label: "pending-verification", badgeType: "warning" },
  PRE_APPROVED: { label: "verified", badgeType: "success" },
  PENDING_APPROVAL: { label: "pending-approval", badgeType: "warning" },
  APPROVED: { label: "approved", badgeType: "success" },
  REJECTED_MARINE_SUPERVISOR: { label: "rejected", badgeType: "error" },
  REJECTED_MARINE_OPERATOR: { label: "rejected", badgeType: "error" },
  INTERRUPTED_IN_PORT: { label: "interrupted-in-port", badgeType: "error" },
  INTERRUPTED_LEAVE_PORT: { label: "interrupted-leave-port", badgeType: "error" },
  PRE_ARRIVAL_DRAFT: { label: "pre-arrival-draft", badgeType: "neutral" },
  ARRIVAL_DRAFT: { label: "arrival-draft", badgeType: "neutral" },
  RESUME_PENDING: { label: "resume-pending", badgeType: "warning" },
  EXPIRED: { label: "expired", badgeType: "error" },
  DEPARTURE_REQUESTED: { label: "departure-requested", badgeType: "warning" },
  DEPARTURE_AWAITING_MARINE_APPROVAL: {
    label: "departure-awaiting-marine-approval",
    badgeType: "warning",
  },
  DEPARTURE_AWAITING_TERMINAL_APPROVAL: {
    label: "departure-awaiting-terminal-approval",
    badgeType: "warning",
  },
  DEPARTURE_APPROVED: { label: "departure-approved", badgeType: "success" },
  DEPARTED: { label: "departed", badgeType: "success" },
  CLOSED: { label: "closed", badgeType: "success" },
  REINSTATED: { label: "reinstated", badgeType: "success" },
  CANCELLED: { label: "cancelled", badgeType: "error" },
};

export const VESSEL_CALL_RESTRICTED_STATUSES = {
  PRE_ARRIVAL_DRAFT: "PRE_ARRIVAL_DRAFT",
  PENDING_VERIFICATION: "PENDING_PRE_APPROVAL",
  REJECTED_MARINE_OPERATOR: "REJECTED_MARINE_OPERATOR",
  REJECTED_MARINE_SUPERVISOR: "REJECTED_MARINE_SUPERVISOR",
  DEPARTED: "DEPARTED",
  CLOSED: "CLOSED",
  CANCELLED: "CANCELLED",
};

const RE_ENTRY_HIDDEN_STATUSES = new Set([
  "PRE_ARRIVAL_DRAFT",
  "PENDING_PRE_APPROVAL",
  "PRE_APPROVED",
  "PENDING_APPROVAL",
  "REJECTED_MARINE_OPERATOR",
  "REJECTED_MARINE_SUPERVISOR",
  "ARRIVAL_DRAFT",
]);

const getReEntryChipSx = (theme: Theme) => {
  return {
    "bgcolor": "#5C4DF4",
    "color": "#FFFFFF",
    "height": 24,
    "borderRadius": 9999,
    "px": 0,
    "minWidth": 60,
    "maxWidth": 80,
    "& .MuiChip-label": {
      paddingLeft: "0.25rem",
      paddingRight: "0.25rem",
      fontSize: 12,
      lineHeight: "20px",
      fontWeight: theme.typography.fontWeightMedium,
      width: "100%",
      display: "block",
      textAlign: "center",
    },
  } as const;
};

function getStatusInfo(rawStatus?: string): { label: string; badgeType: BadgeType } {
  if (!rawStatus) {
    return { label: "common.unknown", badgeType: "warning" };
  }
  const entry = STATUS_MAP[rawStatus];
  if (!entry) {
    return { label: "common.unknown", badgeType: "warning" };
  }
  return { label: `vessel-call.status.${entry.label}`, badgeType: entry.badgeType };
}
export function getStatusLabel(rawStatus?: string): string {
  return getStatusInfo(rawStatus).label;
}
export function buildVesselHeaderDetails(
  id: string | undefined,
  vesselCallDetails: any,
  t: TFunction,
  agencyName?: string,
  vesselRegisterDetailsFixed?: any,
): VesselHeaderDTO {
  return {
    vesselCallId: id!,
    task: vesselCallDetails?.task || null,
    imoNumber: vesselRegisterDetailsFixed?.vesselImoNumber,
    vesselName: vesselRegisterDetailsFixed?.vesselName,
    draftMax: vesselRegisterDetailsFixed?.draftMax,
    type: vesselRegisterDetailsFixed?.vesselTypeDesc?.toUpperCase(),
    countryCode: vesselRegisterDetailsFixed?.countryCode,
    countryName: vesselRegisterDetailsFixed?.countryName,
    agencyName: agencyName,
    dateOfDelivery: vesselRegisterDetailsFixed?.dateOfDelivery,
    loa: vesselRegisterDetailsFixed?.loa,
    beam: vesselRegisterDetailsFixed?.beam,
    vesselCondition: vesselCallDetails?.vesselCondition,
    updatedVesselCondition: vesselCallDetails?.updatedVesselCondition,
    grossTonnage: vesselRegisterDetailsFixed?.grt,
    status: vesselCallDetails?.status,
    restrictions: formatRestriction(vesselRegisterDetailsFixed?.vesselLogTypeCode, t, "-"),
    vesselLocation: vesselCallDetails?.currentLocation,
    lastTaskDecision: vesselCallDetails?.lastTaskDecision,
    companyId: vesselCallDetails?.companyId,
    vesselAge: vesselRegisterDetailsFixed?.vesselAgeYearsAtClose,
    reEntryVessel: vesselCallDetails?.reEntryVessel ?? false,
    hasMovementAnchorTimeOrAllFast: vesselCallDetails?.hasMovementAnchorTimeOrAllFast ?? false,
  };
}

export const isDateOnOrBeforeToday = (date?: string): boolean => {
  if (!date) return false;
  const userDate = new Date(date);
  userDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today >= userDate;
};

export const isDateOnOrAfterToday = (date?: string): boolean => {
  if (!date) return false;
  const userDate = new Date(date);
  userDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today <= userDate;
};

export function getFileExtensionFromUrl(url?: string | null): string {
  return url?.split(".").pop() || "";
}

export function getFileNameFromUrl(url?: string | null): string {
  if (!url) return "";

  const sanitizedUrl = url.split(/[?#]/)[0] ?? "";
  const segments = sanitizedUrl.split("/");
  return decodeURIComponent(segments[segments.length - 1] ?? "");
}

function computeFlag(countryCode?: string, countryName?: string): string {
  const code = countryCode;
  const name = capitalizeFunction(countryName);
  return code && countryName ? `${code} - ${name}` : "-";
}

export function capitalizeFunction(text?: string): string {
  if (!text) return "-";
  const lowered = text.toLowerCase();
  return lowered.charAt(0).toUpperCase() + lowered.slice(1);
}

const normalizeLocation = (location?: string | null): string =>
  location?.trim().toLowerCase() ?? "";

export function shouldUseVesselLocationForArrivalStatus(
  movementTo?: string | null,
  vesselLocation?: string | null,
): boolean {
  const normalizedVesselLocation = normalizeLocation(vesselLocation);

  if (!normalizedVesselLocation) {
    return false;
  }

  return normalizeLocation(movementTo) !== normalizedVesselLocation;
}

interface LabeledField {
  label?: string | React.ReactNode;
  value: React.ReactNode;
}

interface BuildLeftDetailsFieldsParams {
  isMarineDepartment: boolean;
  vesselDetails?: VesselHeaderDTO | null;
  locations: FromToLocation[];
  canSeeViewDetails: boolean;
  viewDetailsLink: React.ReactNode;
  BadgeComponent: React.ComponentType<{ type: BadgeType; label: string; bold?: boolean }>;
  statusOverrideLabel?: string;
  fakeOverrideStatus?: string;
  statusOverrideBadgeType?: BadgeType;
  theme: Theme;
  t: TFunction;
}

function getLocationLabel(currentLocation: string | null | undefined, locations: FromToLocation[]) {
  if (!currentLocation) {
    return undefined;
  }

  const locationDetails = locations.find(
    (location) => location.fromToLocationCode == currentLocation,
  );

  if (!locationDetails) {
    return undefined;
  }

  if (new Set(["ANCH", "BERTH"]).has(locationDetails?.location)) {
    return `${locationDetails.fromToLocationCode} / ${locationDetails.locationName}`;
  }

  if (locationDetails.fromToLocationCode === "SEA") {
    return locationDetails.locationName;
  }

  return locationDetails.fromToLocationCode;
}

export function buildLeftDetailsFields({
  isMarineDepartment,
  vesselDetails,
  locations,
  canSeeViewDetails,
  viewDetailsLink,
  BadgeComponent,
  statusOverrideLabel,
  fakeOverrideStatus,
  statusOverrideBadgeType,
  theme,
  t,
}: BuildLeftDetailsFieldsParams): LabeledField[] {
  const computedStatus = getStatusInfo(vesselDetails?.status);
  const statusInfo = fakeOverrideStatus
    ? getStatusInfo(fakeOverrideStatus)
    : statusOverrideLabel
      ? {
          label: statusOverrideLabel,
          badgeType: statusOverrideBadgeType ?? computedStatus.badgeType,
        }
      : computedStatus;
  const displayChip = vesselDetails?.showReinstatedBadge;
  const base: LabeledField[] = [
    {
      label: (
        <span className="flex items-center justify-between gap-2">
          <span>{t(`${LANG_BASE}.vessel-call-status-header.status`)}</span>
          {displayChip && (
            <Chip
              label={t(`${LANG_BASE}.vessel-call-status-header.reinstated`)}
              sx={{ bgcolor: "#DE1395", color: theme.palette.text.white }}
              size="small"
            />
          )}
        </span>
      ),
      value: (
        <div className="flex flex-col">
          <span className="text-sm">
            <BadgeComponent type={statusInfo.badgeType} label={statusInfo?.label || "-"} bold />
          </span>

          {canSeeViewDetails && (
            <span className="text-xs text-blue-800 pl-4">{viewDetailsLink}</span>
          )}
        </div>
      ),
    },
  ];
  const restrictionDescription = vesselDetails?.restrictions ?? "-";
  const clearToOperateLabel = formatRestriction("CLEARTOOPERATE", t, "-");
  const releasedLabel = formatRestriction("RELEASED", t, "-");
  const interruptedInPortLabel = formatRestriction("INTERRUPTEDINPORT", t, "-");
  const interruptedLeavePortLabel = formatRestriction("INTERRUPTEDLEAVEPORT", t, "-");
  const restrictionsField: LabeledField = {
    label: "vessel-call.vessel-call-status-header.restrictions",
    value: (() => {
      const badgeType: BadgeType =
        vesselDetails?.restrictions === clearToOperateLabel ||
        vesselDetails?.restrictions === releasedLabel
          ? "success"
          : vesselDetails?.restrictions === interruptedInPortLabel ||
              vesselDetails?.restrictions === interruptedLeavePortLabel
            ? "error"
            : "warning";
      return (
        <span className="text-sm flex items-center gap-2">
          <BadgeComponent type={badgeType} label={restrictionDescription} bold />
        </span>
      );
    })(),
  };

  const vesselLocationField: LabeledField = {
    label: `${LANG_BASE}.vessel-call-status-header.vessel-location`,
    value: (
      <span className="text-sm font-bold">
        {getLocationLabel(vesselDetails?.vesselLocation, locations) ?? "-"}
      </span>
    ),
  };
  const shouldShowReEntry =
    vesselDetails?.reEntryVessel && !RE_ENTRY_HIDDEN_STATUSES.has(vesselDetails?.status ?? "");
  const reEntryField: LabeledField = shouldShowReEntry
    ? {
        value: (
          <Chip
            label={t(`${LANG_BASE}.vessel-call-status-header.re-entry`)}
            size="small"
            sx={getReEntryChipSx(theme)}
          />
        ),
      }
    : { value: null };

  if (isMarineDepartment) {
    return [...base, restrictionsField, vesselLocationField, reEntryField];
  }
  return [
    ...base,
    vesselLocationField,
    reEntryField,
    { label: "", value: <span className="min-h-[2.3rem]"></span> },
  ];
}

interface BuildRightDetailFieldsParams {
  vesselDetails?: VesselHeaderDTO | null;
}

export function buildRightDetailsFields({
  vesselDetails,
}: BuildRightDetailFieldsParams): LabeledField[] {
  const flag = computeFlag(vesselDetails?.countryCode, vesselDetails?.countryName);
  const agencyName = vesselDetails?.agencyName;
  return [
    {
      label: `${LANG_BASE}.vessel-call-status-header.imo-number`,
      value: vesselDetails?.imoNumber || "-",
    },
    { label: `${LANG_BASE}.vessel-call-status-header.flag`, value: flag },
    {
      label: `${LANG_BASE}.vessel-call-status-header.loa`,
      value: vesselDetails?.loa || "-",
    },
    {
      label: `${LANG_BASE}.vessel-call-status-header.vessel-condition`,
      value: vesselDetails?.updatedVesselCondition ?? vesselDetails?.vesselCondition ?? "-",
    },
    {
      label: `${LANG_BASE}.vessel-call-status-header.vessel-name`,
      value: vesselDetails?.vesselName || "-",
    },
    {
      label: `${LANG_BASE}.vessel-call-status-header.agency`,
      value: capitalizeWord(agencyName || "-"),
    },
    {
      label: `${LANG_BASE}.vessel-call-status-header.ship-beam`,
      value: vesselDetails?.beam || "-",
    },
    {
      label: `${LANG_BASE}.vessel-call-status-header.gross-tonnage`,
      value: vesselDetails?.grossTonnage || "-",
    },
    {
      label: `${LANG_BASE}.vessel-call-status-header.type`,
      value: vesselDetails?.type || "-",
    },
    { label: `${LANG_BASE}.vessel-call-status-header.age`, value: vesselDetails?.vesselAge },
    {
      label: `${LANG_BASE}.vessel-call-status-header.ship-max-draft`,
      value: vesselDetails?.draftMax || "-",
    },
  ];
}

export function fullNameFromPortCode(portCode: string): string {
  const portNames: Record<string, string> = {
    POF: "PORTOFFUJAIRAH",
    POD: "PORTOFDIBBA",
  };
  return portNames[portCode];
}
export function portCodeFromFullName(portCode: string | undefined): string | undefined {
  if (!portCode) return undefined;

  const portCodes: Record<string, string> = {
    PORTOFFUJAIRAH: "POF",
    PORTOFDIBBA: "POD",
  };

  return portCodes[portCode];
}

export function mapVesselCallToForm(vc: any): VesselCallNotificationDTO {
  return {
    expectedArrivalLocation: vc?.expectedArrivalLocation ?? "",
    selectPort: vc?.selectPort ?? "",
    masterName: vc?.generalDeclaration?.masterName ?? vc?.masterName ?? "",
    nationality: vc?.nationality ?? "",
    eta: vc?.eta ?? "",
    etd: vc?.etd ?? "",
    lastPort: vc?.lastPort ?? "",
    nextPort: vc?.nextPort ?? "",
    draftFwd: vc?.draftFwd ?? undefined,
    draftAft: vc?.draftAft ?? undefined,
    ownersMattersAgent: vc?.ownersMattersAgent ?? "",
    vesselCondition: vc?.vesselCondition ?? "",

    cargoDescription: vc?.cargoDescription ?? "",
    cargoOnboard: vc?.cargoOnboard ?? [],
    cargoDangerous: vc?.cargoDangerous ?? false,
    cargoQuantity: vc?.cargoQuantity ?? undefined,
    cargoImoHazardClasses: vc?.cargoImoHazardClasses ?? "",
    natureOfCall: vc?.natureOfCall ?? undefined,
    unitOfMeasurement: vc?.cargoMeasurementUnit ?? vc?.unitOfMeasurement ?? "",
    imo2020Compliance: vc?.imo2020Compliance ?? false,

    portFacility: vc?.portFacility ?? "",
    portFacilitySpecify: vc?.portFacilitySpecify ?? "",
    securityLevel: vc?.securityLevel ?? "",
    shipPortSecurityMeasuresTaken: vc?.shipPortSecurityMeasuresTaken ?? false,
    isscValid: vc?.isscValid ?? false,
    takenMeasures: vc?.takenMeasures ?? "",
    isscNumber: vc?.isscNumber ?? "",
    securityGuardsOnBoard: vc?.securityGuardsOnBoard ?? false,
    noOfGuards: vc?.noOfGuards ?? undefined,
    armAmmunitionOnBoard: vc?.armAmmunitionOnBoard ?? false,
    isscIssuedBy: vc?.isscIssuedBy ?? "",
    isscIssueDate: vc?.isscIssueDate ?? "",
    isscExpiryDate: vc?.isscExpiryDate ?? "",
    vesselHasDeficiencies: vc?.vesselHasDeficiencies ?? false,
    deficiencies: vc?.deficiencies ?? "",
    sspOnboardApproved: vc?.sspOnboardApproved ?? false,

    portCalls: vc?.portCalls ?? [],

    armAmmunitionProofDocumentUrl: vc?.armAmmunitionProofDocumentUrl ?? "",
    deficienciesDocumentsUrl: vc?.deficienciesDocumentsUrl ?? "",
    flagStatePermissionDocumentUrl: vc?.flagStatePermissionDocumentUrl ?? "",
    lastPortClearanceDocumentUrl: vc?.lastPortClearanceDocumentUrl ?? "",
    lastPortClearanceDocumentName: vc?.lastPortClearanceDocumentName ?? "",
    lastPortClearanceClassName: vc?.lastPortClearanceClassName ?? "",
  };
}
export function formatDateTime(dateString?: string | null): string {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy}\n${hh}:${min}`;
}

export function calculateShipAge(dateOfDelivery?: string, t?: TFunction): number | string {
  if (!dateOfDelivery) return 0;

  const deliveryDate = new Date(dateOfDelivery);
  if (isNaN(deliveryDate.getTime())) return 0;

  const currentDate = new Date();

  // Check if the date is in the future
  if (deliveryDate > currentDate) {
    return t ? t("common.invalid-year") : "common.invalid-year";
  }

  let age = currentDate.getFullYear() - deliveryDate.getFullYear();

  // Check if the anniversary has occurred this year
  const anniversaryThisYear = new Date(
    currentDate.getFullYear(),
    deliveryDate.getMonth(),
    deliveryDate.getDate(),
  );

  if (currentDate < anniversaryThisYear) {
    age--;
  }

  return age;
}
